import React, { useCallback, useState } from 'react';
import { Typography } from '@vtblife/uikit';
import { Size } from '@vtblife/uikit/legacy';
import {
    SuggestTypeEnum,
    SuggestItemDeveloper
} from '@search/graphql-typings';
import { graphql, useGqlContext } from '@search/gql-client/src';
import { Filter } from '@search/vtbeco-frontend-core/view/filters/components/filter/Filter';

import Suggest from '@search/vtbeco-ui/src/components/controls/Suggest';
import { Highlight } from '@search/vtbeco-ui/src/components/controls/Highlight/Highlight';
import useDebouncedCallback from '@search/vtbeco-frontend-core/view/common/hooks/useDebouncedCallback';
import classname from '@search/classname/src';

import './DesktopDeveloperFilterView.css';
import {
    DesktopDeveloperFilterViewQuery$data as DeveloperFilterViewQueryResponse,
    DesktopDeveloperFilterViewQuery$variables as DeveloperFilterViewQueryVariables
} from './__generated__/DesktopDeveloperFilterViewQuery.graphql';

interface IProps {
    value?: SuggestItemDeveloper;
    onChange: (value?: SuggestItemDeveloper) => void;
    placeholder?: string;
}

const cn = classname.bind(null, 'DesktopDeveloperFilterView');

const QUERY = graphql`
    query DesktopDeveloperFilterViewQuery($text: String! $suggestTypes: [SuggestTypeEnum!]) {
        suggest(text: $text suggestTypes: $suggestTypes) {
            ...on SuggestItemDeveloper {
                label
                data {
                    id
                    scope
                }
                developer {
                    id
                    title
                }
            }
        }
    }
`;

export const DesktopDeveloperFilterView: React.FC<IProps> = ({
    value,
    onChange,
    placeholder = 'Название компании'
}) => {
    const gql = useGqlContext();

    const handleSelect = useCallback((val?: SuggestItemDeveloper) => {
        onChange(val);
    }, [ onChange ]);

    const handleClear = useCallback(() => {
        onChange();
    }, [ onChange ]);

    const [ suggestions, setSuggestions ] = useState<SuggestItemDeveloper[]>([]);

    const [ debouncedSuggest ] = useDebouncedCallback(
        (text: string) => {
            gql.execute<DeveloperFilterViewQueryVariables, DeveloperFilterViewQueryResponse>(
                QUERY, {
                    text,
                    suggestTypes: [ SuggestTypeEnum.Developer ]
                })
                .then(data => {
                    if (data.data) {
                        setSuggestions(data?.data?.suggest || []);
                    } else {
                        setSuggestions([]);
                    }
                }).catch(() => setSuggestions([]));
        },
        300
    );

    const fetchSuggestions = (text: string) => {
        debouncedSuggest(text);
    };

    return (
        <Filter filterTitle='Застройщик' className={cn('select')}>
            <Suggest
                width='max'
                // @ts-ignore
                size='xxl'
                inputSize={Size.Small}
                borderOut={false}
                placeholder={placeholder}
                suggestion={value}
                suggestions={suggestions}
                onClear={handleClear}
                fetchSuggestions={fetchSuggestions}
                renderList={(props, { focusedIndex, label }, handlers) => (
                    <div className={cn('suggest')}>
                        {
                            suggestions.map((suggestion, i) => (
                                <div
                                    key={i}
                                    className={cn('suggestion', {
                                        highlighted: i === focusedIndex
                                    })}
                                    onMouseDown={e => handlers.handleItemMouseDown(e, i)}
                                >
                                    <div>
                                        <Highlight
                                            text={suggestion.label || ''}
                                            highlight={label || ''}
                                        />
                                        {
                                            suggestion?.data?.scope ? (
                                                <Typography
                                                    variant='small-alone'
                                                    color='secondary'
                                                >
                                                    {suggestion.data.scope}
                                                </Typography>
                                            ) : null
                                        }
                                    </div>
                                </div>
                            ))
                        }
                    </div>
                )}
                renderEmptyList={({ emptyText }) => (
                    <div className={cn('empty')}>
                        {emptyText}
                    </div>
                )}
                onSelect={handleSelect}
                clearOnSelect={false}
            />
        </Filter>
    );
};
